import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Text,
    Card,
    CardBody,
    Flex,
    Grid,
    GridItem,
    VStack,
    useDisclosure,
    useToast,
    Spinner,
    Center,
    Tag
} from '@chakra-ui/react';
import {
    FiUpload,
    FiFileText,
    FiSend,
    FiCheckCircle,
    FiZap,
    FiEye,
    FiPlay,
    FiTrash2,
    FiActivity,
} from 'react-icons/fi';

// Note: This component requires axios and CampaignModal to be imported in your actual implementation
import axios from 'axios';
import { getApiHeaders, formatNumber } from '../../../utils/DefaultFunction';
import { CampaignModal } from './Components/CampaignModal';
import { CampaignLogsModal } from './Components/CampaignActivityLog';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import Header from '../../../components/Header';
import { useNavigate } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import { PRIMARY_BG } from '../../../theme/brand';

export default function CSVMessageSender() {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isLogsOpen, onOpen: onLogsOpen, onClose: onLogsClose } = useDisclosure();
    const [logsCampaign, setLogsCampaign] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [channels, setChannels] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
    const [stats, setStats] = useState({
        total: 0,
        completed: 0,
        active: 0,
        draft: 0,
    });
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        fetchTemplates();
        loadChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
            // Replace with your axios implementation
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/template/msg-temp-type-ids`,
                { headers: getApiHeaders() }
            );

            const data = await response.json();
            if (data) {
                setTemplates(data.data || data);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            toast({
                title: "Error",
                description: "Failed to fetch templates",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingTemplates(false);
        }
    };

    const loadChannels = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/channel/list`;
            const response = await fetch(url, { headers: getApiHeaders() });
            const res = await response.json();

            const apiChannels = (res?.data || []).map((channel) => ({
                id: channel.id,
                name: channel.name,
                description: 'No description',
                color: channel.color || 'blue',
                features: ['Basic Messaging'],
                limitations: '',
                displayName: channel.name,
                ...channel,
            }));

            const formatted = [...apiChannels];
            setChannels(formatted);
        } catch (err) {
            console.error('Failed to load channels', err);
            toast({
                title: "Error",
                description: "Failed to load channels",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const fetchCampaigns = async () => {
        setLoadingCampaigns(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/csv/msg-list?page=${currentPage}&limit=${limit}`,
                { headers: getApiHeaders() }
            );
            const data = await response.json();

            if (data && data.data) {
                setCampaigns(data.data);

                if (data.stats) {
                    setStats({
                        total: data.stats.total || 0,
                        completed: data.stats.completed || 0,
                        active: data.stats.active || 0,
                        draft: data.stats.draft || 0,
                    });
                }

                if (data.pagination) {
                    setPagination(data.pagination);
                }
            }
        } catch (error) {
            console.error('Error fetching campaigns:', error);
            toast({
                title: "Error",
                description: "Failed to fetch campaigns",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingCampaigns(false);
        }
    };

    const getChannelColor = (channelName) => {
        const channel = channels.find(c => c.name.toLowerCase() === channelName?.toLowerCase());
        // PRIMARY_BG, not var(--brand-primary): the caller builds an alpha colour by
        // concatenation (`${channelColor}20`), and a CSS variable cannot be extended
        // that way — it would produce the invalid value "var(--brand-primary)20".
        return channel?.color || PRIMARY_BG;
    };

    const getStatusBadge = (statusId, onClick) => {
        const statusMap = {
            'completed': { label: 'Completed', colorScheme: 'green' },
            'processing': { label: 'In Progress', colorScheme: 'orange' },
            'pending': { label: 'Pending', colorScheme: 'yellow' },
            'failed': { label: 'Failed', colorScheme: 'red' },
            'Draft': { label: 'Draft', colorScheme: 'orange' },
        };

        const status = statusMap[statusId] || { label: statusId, colorScheme: 'gray' };

        return (
            <Tag
                colorScheme={status.colorScheme}
                px={2}
                py={1}
                cursor={statusId === 'Draft' || statusId === 'processing' || statusId === 'pending' || statusId === 'completed' ? 'pointer' : 'default'}
                onClick={onClick}
                _hover={statusId === 'Draft' || statusId === 'processing' || statusId === 'pending' || statusId === 'completed' ? { opacity: 0.8 } : {}}
            >
                {status.label}
            </Tag>
        );
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleCampaignComplete = () => {
        // Refresh campaign list when a campaign completes
        fetchCampaigns();
    };

    const handleCampaignClick = (campaign) => {
        setSelectedCampaign(campaign);
        onOpen();
    };

    const handleNewCampaign = () => {
        setSelectedCampaign(null);
        onOpen();
    };

    const handleModalClose = () => {
        setSelectedCampaign(null);
        onClose();
    };

    const handleViewReport = (id) => {
        navigate(`/tools/csv-message-sender-report/${id}`)
    };

    const handleViewLogs = (campaign) => {
        setLogsCampaign(campaign);
        onLogsOpen();
    };

    const handleLogsClose = () => {
        onLogsClose();
        setLogsCampaign(null);
    };

    const handleDeleteDraft = async (csvId) => {
        try {
            setDeletingId(csvId);
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/csv/msg-delete/${csvId}`,
                { headers: getApiHeaders() }
            );
            toast({
                title: 'Deleted',
                description: 'Draft campaign deleted successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            fetchCampaigns();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to delete draft',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setDeletingId(null);
        }
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleContinueDraft = (campaign) => {
        setSelectedCampaign(campaign);
        onOpen();
    };

    return (
        <>
            <Header />
            <BoxWrapper>
                {/* Header */}
                <Flex justify="space-between" align="flex-start" mb={6}>
                    <Box>
                        <Flex align="center" gap={2} mb={2}>
                            <FiFileText size={28} color="var(--brand-primary)" />
                            <Text fontSize="2xl" fontWeight="bold">CSV Message Sender</Text>
                        </Flex>
                        <Text color="gray.600">
                            Upload a CSV file and send bulk template-based messages without integration
                        </Text>
                    </Box>
                    <Button
                        onClick={handleNewCampaign}
                        size="sm"
                        bg="var(--brand-primary)"
                        color="white"
                        _hover={{ bg: "var(--brand-primary-hover)" }}
                        leftIcon={<FiUpload />}
                        isLoading={loadingTemplates}
                        flexShrink={0}
                    >
                        New CSV Campaign
                    </Button>
                </Flex>

                {/* Stats Cards */}
                <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
                    <GridItem>
                        <Box className='box-style-card'>
                            <Box>
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text color="gray.600" mb={1} fontSize="sm">Total Campaigns</Text>
                                        <Text fontSize="2xl" fontWeight="semibold">{formatNumber(stats.total)}</Text>
                                    </Box>
                                    <Flex
                                        w="48px"
                                        h="48px"
                                        bg="blue.50"
                                        borderRadius="lg"
                                        align="center"
                                        justify="center"
                                    >
                                        <FiSend size={24} color="var(--brand-primary)" />
                                    </Flex>
                                </Flex>
                            </Box>
                        </Box>
                    </GridItem>

                    <GridItem>
                        <Box className='box-style-card'>
                            <Box>
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text color="gray.600" mb={1} fontSize="sm">Completed</Text>
                                        <Text fontSize="2xl" fontWeight="semibold">{formatNumber(stats.completed)}</Text>
                                    </Box>
                                    <Flex
                                        w="48px"
                                        h="48px"
                                        bg="green.50"
                                        borderRadius="lg"
                                        align="center"
                                        justify="center"
                                    >
                                        <FiCheckCircle size={24} color="#38A169" />
                                    </Flex>
                                </Flex>
                            </Box>
                        </Box>
                    </GridItem>

                    <GridItem>
                        <Box className='box-style-card'>
                            <Box>
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text color="gray.600" mb={1} fontSize="sm">Active</Text>
                                        <Text fontSize="2xl" fontWeight="semibold">{formatNumber(stats.active)}</Text>
                                    </Box>
                                    <Flex
                                        w="48px"
                                        h="48px"
                                        bg="orange.50"
                                        borderRadius="lg"
                                        align="center"
                                        justify="center"
                                    >
                                        <FiZap size={24} color="#DD6B20" />
                                    </Flex>
                                </Flex>
                            </Box>
                        </Box>
                    </GridItem>

                    <GridItem>
                        <Box className='box-style-card'>
                            <Box>
                                <Flex justify="space-between" align="center">
                                    <Box>
                                        <Text color="gray.600" mb={1} fontSize="sm">Draft</Text>
                                        <Text fontSize="2xl" fontWeight="semibold">{formatNumber(stats.draft)}</Text>
                                    </Box>
                                    <Flex
                                        w="48px"
                                        h="48px"
                                        bg="purple.50"
                                        borderRadius="lg"
                                        align="center"
                                        justify="center"
                                    >
                                        <FiFileText size={24} color="#805AD5" />
                                    </Flex>
                                </Flex>
                            </Box>
                        </Box>
                    </GridItem>
                </Grid>

                {/* Recent Campaigns Table */}
                <Box className='box-style-card'>
                    <Box>
                        <Text fontWeight="medium" mb={4}>All Campaigns</Text>

                        {loadingCampaigns ? (
                            <Center py={10}>
                                <Spinner size="lg" color="var(--brand-primary)" />
                            </Center>
                        ) : campaigns.length === 0 ? (
                            <Center py={10}>
                                <VStack spacing={3}>
                                    <FiFileText size={48} color="#CBD5E0" />
                                    <Text color="gray.500">No campaigns yet</Text>
                                    <Text fontSize="sm" color="gray.400">
                                        Click "New CSV Campaign" to get started
                                    </Text>
                                </VStack>
                            </Center>
                        ) : (
                            <>
                            <VStack spacing={3} align="stretch" mb={4}>
                                {campaigns.map((campaign, index) => {
                                    const template = templates.find(t => t.id === campaign.template_id);
                                    const channelColor = getChannelColor(campaign.prefered_channel);
                                    const successRate = campaign.process_count > 0
                                        ? ((campaign.success_count / campaign.process_count) * 100).toFixed(1)
                                        : 0;
                                    const isDraft = campaign.status_id === 'Draft';

                                    return (
                                        <Card
                                            key={campaign._id || index}
                                            variant="outline"
                                            bg={isDraft ? '#FFF5E6' : 'white'}
                                            borderColor={isDraft ? '#FFA500' : undefined}
                                            borderWidth={isDraft ? '1px' : undefined}
                                        >
                                            <CardBody>
                                                <Flex justify="space-between" align="center">
                                                    <Box flex={1}>
                                                        <Flex align="center" gap={3} mb={2}>
                                                            <Text fontWeight="medium">
                                                                {campaign.name ? campaign.name.replace(/^csv-\d+-\d+\.csv$/, 'Campaign File').replace('.csv', '') : `Campaign ${index + 1}`}
                                                            </Text>
                                                            {campaign.prefered_channel && (
                                                                <Tag
                                                                    variant="subtle"
                                                                    px={2}
                                                                    py={1}
                                                                    borderRadius="md"
                                                                    style={{
                                                                        backgroundColor: `${channelColor}20`,
                                                                        color: channelColor
                                                                    }}
                                                                >
                                                                    {campaign.prefered_channel?.toUpperCase()}
                                                                </Tag>
                                                            )}
                                                            {getStatusBadge(campaign.status_id, () => handleCampaignClick(campaign))}
                                                        </Flex>
                                                        <Text fontSize="xs" color="gray.600" mb={2}>
                                                            Template: {template?.name || `Template ID ${campaign.template_id}`}
                                                        </Text>
                                                        <Flex gap={4} fontSize="xs" flexWrap="wrap">
                                                            <Text color="gray.600">
                                                                Total: <Text as="span" fontWeight="medium" color="gray.900">
                                                                    {formatNumber(campaign.total_rows || 0)}
                                                                </Text>
                                                            </Text>
                                                            <Text color="gray.600">
                                                                Processed: <Text as="span" fontWeight="medium" color="blue.600">
                                                                    {formatNumber(campaign.process_count || 0)}
                                                                </Text>
                                                            </Text>
                                                            <Text color="gray.600">
                                                                Success: <Text as="span" fontWeight="medium" color="green.600">
                                                                    {formatNumber(campaign.success_count || 0)}
                                                                </Text>
                                                            </Text>
                                                            {campaign.error_count > 0 && (
                                                                <Text color="gray.600">
                                                                    Failed: <Text as="span" fontWeight="medium" color="red.600">
                                                                        {formatNumber(campaign.error_count)}
                                                                    </Text>
                                                                </Text>
                                                            )}
                                                            {campaign.process_count > 0 && (
                                                                <Text color="gray.600">
                                                                    Success Rate: <Text as="span" fontWeight="medium" color="green.600">
                                                                        {successRate}%
                                                                    </Text>
                                                                </Text>
                                                            )}
                                                            <Text color="gray.600">
                                                                Date: <Text as="span" fontWeight="medium" color="gray.900">
                                                                    {formatDate(campaign.created_at)}
                                                                </Text>
                                                            </Text>
                                                        </Flex>
                                                    </Box>
                                                    <Flex gap={2}>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            leftIcon={<FiActivity />}
                                                            onClick={() => handleViewLogs(campaign)}
                                                        >
                                                            Logs
                                                        </Button>
                                                        {isDraft ? (
                                                            <>
                                                                <Button
                                                                    colorScheme="red"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    leftIcon={<FiTrash2 />}
                                                                    isLoading={deletingId === campaign._id}
                                                                    onClick={() => handleDeleteDraft(campaign._id)}
                                                                >
                                                                    Delete
                                                                </Button>
                                                                <Button
                                                                    colorScheme="orange"
                                                                    size="sm"
                                                                    leftIcon={<FiPlay />}
                                                                    onClick={() => handleContinueDraft(campaign)}
                                                                >
                                                                    Continue
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                leftIcon={<FiEye />}
                                                                onClick={() => handleViewReport(campaign._id)}
                                                            >
                                                                View Report
                                                            </Button>
                                                        )}
                                                    </Flex>
                                                </Flex>
                                            </CardBody>
                                        </Card>
                                    );
                                })}
                            </VStack>

                            {pagination.total > limit && (
                                <Box mt={4} display="flex" justifyContent="center">
                                    <Pagination
                                        activePage={currentPage}
                                        itemsCountPerPage={limit}
                                        totalItemsCount={pagination.total}
                                        pageRangeDisplayed={5}
                                        onChange={handlePageChange}
                                        itemClass="page-item"
                                        linkClass="page-link"
                                        activeClass="active"
                                        activeLinkClass="active-link"
                                    />
                                </Box>
                            )}
                            </>
                        )}
                    </Box>
                </Box>

                {/* Campaign Modal - Replace with your actual CampaignModal component */}
                <CampaignModal
                    isOpen={isOpen}
                    onClose={onClose}
                    templates={templates}
                    channels={channels}
                    onCampaignComplete={handleCampaignComplete}
                    existingCampaign={selectedCampaign}
                />

                {/* Activity log for a single campaign */}
                <CampaignLogsModal
                    isOpen={isLogsOpen}
                    onClose={handleLogsClose}
                    campaign={logsCampaign}
                />
            </BoxWrapper>
        </>
    );
}